import { Box, Button, Typography } from "@mui/material";
import {
  formatEvaluation,
  type MoveClassification,
  type MoveEvidenceFactKind,
  type MoveEvidencePacket,
} from "@chessla/shared";
import {
  chessArrow,
  isChessSquare,
  PLAYED_MOVE_ARROW_COLOR,
  PLAYED_MOVE_SQUARE_TINT,
  type ChessArrow,
  type ChessSquareStyles,
} from "@/shared/components/chessboard";

export type MoveEvidenceLayer = "played" | "better" | "all";

export const moveEvidencePosition = (packet: MoveEvidencePacket, layer: MoveEvidenceLayer): string =>
  layer === "better" ? packet.identity.fenBefore : packet.identity.fenAfter;

export const moveEvidenceEvaluation = (packet: MoveEvidencePacket, layer: MoveEvidenceLayer) =>
  layer === "better" ? packet.objective.evaluationBefore : packet.objective.evaluationAfter;

export const matchingMoveEvidence = (
  move: { moveNumber: number; san: string; fenBefore: string; fenAfter: string } | null,
  packet: MoveEvidencePacket | null | undefined,
): MoveEvidencePacket | null => {
  if (!move || !packet) return null;
  return packet.identity.moveNumber === move.moveNumber &&
    packet.identity.san === move.san &&
    packet.identity.fenBefore === move.fenBefore &&
    packet.identity.fenAfter === move.fenAfter
    ? packet
    : null;
};

export const visibleMoveEvidence = (
  packet: MoveEvidencePacket | null | undefined,
  reveal: boolean,
  temporaryActive: boolean,
  alternatePosition: boolean,
): MoveEvidencePacket | null => (reveal && !temporaryActive && !alternatePosition ? (packet ?? null) : null);

export { PLAYED_MOVE_ARROW_COLOR, PLAYED_MOVE_SQUARE_TINT } from "@/shared/components/chessboard";
const arrowColor = { played: PLAYED_MOVE_ARROW_COLOR, best: "#f0ece2" } as const;
const relationshipColor = (kind: MoveEvidenceFactKind | undefined): string =>
  kind?.startsWith("pin")
    ? "rgba(155,123,213,0.58)"
    : kind?.startsWith("support")
      ? "rgba(95,145,201,0.58)"
      : "rgba(255,61,18,0.58)";

export const moveEvidenceBoardPresentation = (
  packet: MoveEvidencePacket,
  layer: MoveEvidenceLayer,
): { arrows: ChessArrow[]; squareStyles: ChessSquareStyles; description: string } => {
  const visible = packet.markers.filter(
    (marker) =>
      marker.kind === "played" ||
      (layer === "better" && marker.kind === "best") ||
      (layer === "all" && marker.kind === "relationship"),
  );
  const arrows = visible.flatMap((marker) =>
    marker.kind !== "relationship" && marker.from && marker.to && isChessSquare(marker.from) && isChessSquare(marker.to)
      ? [
          chessArrow(marker.from, marker.to, arrowColor[marker.kind], {
            label: marker.kind === "played" ? "Played move" : "Verified alternative",
            description: `${marker.label}: ${marker.from} → ${marker.to}.`,
          }),
        ]
      : [],
  );
  const squareStyles = Object.fromEntries(
    visible.flatMap((marker) => {
      if (marker.kind === "relationship" && marker.square && isChessSquare(marker.square))
        return [[marker.square, { backgroundColor: relationshipColor(marker.factKind) }]];
      if (marker.kind !== "played") return [];
      return [marker.from, marker.to]
        .filter(isChessSquare)
        .map((square) => [square, { backgroundColor: PLAYED_MOVE_SQUARE_TINT }]);
    }),
  ) as ChessSquareStyles;
  return {
    arrows: arrows.slice(0, 3),
    squareStyles,
    description: visible
      .map((marker) =>
        marker.square ? `${marker.label} on ${marker.square}` : `${marker.label} ${marker.from}–${marker.to}`,
      )
      .join(". "),
  };
};

const labels: Record<MoveEvidenceLayer, string> = {
  played: "Played",
  better: "Better",
  all: "All relationships",
};

export const nikolaMoveVerdict = (classification: MoveClassification): string =>
  classification === "imprecision" || classification === "mistake" || classification === "blunder"
    ? "NIKOLA DISAPPROVES"
    : "NIKOLA APPROVES";

export const MoveEvidenceStrip = ({
  packet,
  layer,
  onLayerChange,
}: {
  packet: MoveEvidencePacket;
  layer: MoveEvidenceLayer;
  onLayerChange: (layer: MoveEvidenceLayer) => void;
}) => (
  <Box
    data-testid="move-evidence-strip"
    sx={{ borderBlock: "1px solid", borderColor: "divider", px: { xs: 1.25, sm: 1.75 }, py: 1.25 }}
  >
    <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
      <Typography sx={{ color: "primary.main", fontSize: "0.7rem", fontWeight: 900, letterSpacing: "0.09em" }}>
        {nikolaMoveVerdict(packet.objective.classification)} · {packet.objective.classification.toUpperCase()}
      </Typography>
      <Typography sx={{ color: "text.secondary", fontSize: "0.64rem", fontWeight: 800, letterSpacing: "0.06em" }}>
        VERIFIED ANALYSIS{packet.objective.analysisDepth != null ? ` · DEPTH ${packet.objective.analysisDepth}` : ""}
      </Typography>
    </Box>
    <Typography sx={{ mt: 0.55, fontSize: "0.82rem", lineHeight: 1.5 }}>{packet.summary}</Typography>
    <Box sx={{ mt: 0.75, display: "flex", gap: 1.5, flexWrap: "wrap", color: "text.secondary" }}>
      {packet.objective.bestMove && (
        <Typography sx={{ fontSize: "0.68rem" }}>NIKOLA PREFERS · {packet.objective.bestMove.san}</Typography>
      )}
      {packet.objective.evaluationAfter && (
        <Typography sx={{ fontSize: "0.68rem" }}>
          AFTER · {formatEvaluation(packet.objective.evaluationAfter)}
        </Typography>
      )}
    </Box>
    <Box
      aria-label="Board evidence layer"
      sx={{
        mt: 1,
        display: "grid",
        gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(4, minmax(0, 1fr))" },
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      {(Object.keys(labels) as MoveEvidenceLayer[]).map((value) => (
        <Button
          key={value}
          aria-pressed={layer === value}
          disabled={value === "better" && !packet.objective.bestMove}
          onClick={() => onLayerChange(value)}
          sx={{
            minHeight: 44,
            borderRadius: 0,
            borderInlineEnd: "1px solid",
            borderColor: "divider",
            fontSize: "0.64rem",
            "&[aria-pressed='true']": { bgcolor: "primary.main", color: "primary.contrastText" },
          }}
        >
          {labels[value]}
        </Button>
      ))}
    </Box>
  </Box>
);
